/* eslint-disable @typescript-eslint/no-unused-vars */
import { BoxWrapper } from 'components/Base';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { CustomerType } from 'store/modules/Customers/types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import Tabs from '@material-ui/core/Tabs';
import Tab from 'components/Base/Tabs/FixedTab';

export interface RiskProfileTypes {
  caseCustomers: Array<CustomerType>;
  userResponses?: any;
}

const useStyles = makeStyles({
  tableCell: {
    position: 'relative'
  },
  row: {
    borderBottom: '1px solid #e0e0e0'
  },
  averageColumn: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#e7e7e8 !important',
      opacity: 0.7,
      zIndex: 1
    }
  },
  averageHeaderCell: {
    backgroundColor: '#e7e7e8 !important',
    color: '#000 !important'
  },
  headerCell: {
    textAlign: 'center',
    verticalAlign: 'middle'
  },
  dot: {
    display: 'inline-block',
    width: 15,
    height: 15,
    borderRadius: '50%',
    position: 'relative',
    zIndex: 1
  },
  rowLabel: {
    fontWeight: 'bold',
    minWidth: '100px'
  },
  cell: {
    fontFamily: 'Gilroy'
  }
});

const riskGroupToColumnIndex = (group: string) => {
  const groupToIndexMap: Record<string, number> = {
    'Very Low': 1,
    Low: 2,
    Average: 3,
    High: 4,
    'Very High': 5
  };

  return groupToIndexMap[group];
};

const scoreToColumnIndex = (score: number, group: string) => {
  const baseIndex = riskGroupToColumnIndex(group);
  return Math.max(1, Math.min(5, baseIndex + score));
};

const scoreToDotColor = (score: number) => {
  if (score === -3 || score === -2 || score === 2 || score === 3)
    return '#c00101';
  if (score === -1 || score === 1) return '#ffc100';
  if (score === 0) return '#4da72e';
};

const MorningstarClientDifferences: React.FC<RiskProfileTypes> = ({
  caseCustomers,
  userResponses
}) => {
  const classes = useStyles();
  const [selectedCustomer, setSelectedCustomer] = useState('first');

  const isMoreThanOneCustomer = caseCustomers.length > 1;
  const customers = isMoreThanOneCustomer
    ? caseCustomers.filter((customer: CustomerType) => !customer.is_jointed)
    : [];

  const firstCustomer = customers[0];
  const secondCustomer = customers[1];
  const jointCustomer = caseCustomers.find(c => c.is_jointed);
  const isUserResponsesArray = Array.isArray(userResponses);

  let selectedUserResponseIndex = 0;

  if (selectedCustomer === 'joint' && jointCustomer && isUserResponsesArray) {
    const jointIndex = userResponses.findIndex(
      (user: any) => user.customer_id === jointCustomer.id
    );
    selectedUserResponseIndex = jointIndex !== -1 ? jointIndex : 0;
  } else if (isUserResponsesArray) {
    const selectedCustomerId =
      selectedCustomer === 'first' ? firstCustomer?.id : secondCustomer?.id;

    const individualIndex = userResponses.findIndex(
      (user: any) => user.customer_id === selectedCustomerId
    );

    selectedUserResponseIndex = individualIndex !== -1 ? individualIndex : 0;
  }

  const keyDifferencesMap = userResponses?.[selectedUserResponseIndex]?.raw_data
    ?.score?.keyDifferencesMap as Record<string, number>;

  const calculatedRiskGroup =
    userResponses?.[selectedUserResponseIndex]?.raw_data?.score
      ?.calculatedRiskGroup;

  const reversedEntries = Object.entries(keyDifferencesMap || {}).reverse();

  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    newCustomer: string | null
  ) => {
    if (newCustomer !== null) {
      setSelectedCustomer(newCustomer);
    }
  };

  const shadedColumnIndex = riskGroupToColumnIndex(calculatedRiskGroup);

  const hasFM10Keys =
    keyDifferencesMap &&
    Object.keys(keyDifferencesMap).some(key => key.startsWith('FM10'));

  return (
    <BoxWrapper
      title="Your Differences"
      tooltip={
        'It is not uncommon for some of the answers given in the risk tolerance questionnaire to differ from those typically given by people with similar risk tolerance. Sometimes the difference indicates more risk tolerance and sometimes less.'
      }
      headerChildren={
        customers.length > 0 && (
          <Tabs
            value={selectedCustomer}
            onChange={(_, newValue) => setSelectedCustomer(newValue)}
            style={{
              marginBottom: '-34px',
              minHeight: 'unset',
              height: '40px'
            }}
            TabIndicatorProps={{
              style: { display: 'none' }
            }}
          >
            <Tab
              label={
                firstCustomer ? firstCustomer.display_name : 'First Customer'
              }
              value="first"
              style={{
                textTransform: 'initial',
                minHeight: 'unset',
                height: '40px'
              }}
            />
            <Tab
              label={
                secondCustomer ? secondCustomer.display_name : 'Second Customer'
              }
              value="second"
              style={{
                textTransform: 'initial',
                minHeight: 'unset',
                height: '40px'
              }}
            />
            {jointCustomer && (
              <Tab
                label="Joint"
                value="joint"
                style={{
                  textTransform: 'initial',
                  minHeight: 'unset',
                  height: '40px'
                }}
              />
            )}
          </Tabs>
        )
      }
      style={{ height: '50vh', overflow: 'hidden' }}
    >
      {hasFM10Keys ? (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell} />
                {['Very Low', 'Low', 'Average', 'High', 'Very High'].map(
                  (label, index) => (
                    <TableCell
                      key={label}
                      style={{ textAlign: 'center' }}
                      className={`${classes.cell} ${classes.headerCell} ${
                        shadedColumnIndex === index + 1
                          ? classes.averageHeaderCell
                          : ''
                      }`}
                    >
                      {label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {reversedEntries.map(([_, score], index) => (
                <TableRow key={`Q${index + 1}`} className={classes.row}>
                  <TableCell className={classes.rowLabel}>
                    {`Q${index + 1}`}
                  </TableCell>
                  {Array.from({ length: 5 }, (_, cellIndex) => (
                    <TableCell
                      key={cellIndex}
                      className={`${classes.cell} ${classes.tableCell} ${
                        cellIndex + 1 === shadedColumnIndex
                          ? classes.averageColumn
                          : ''
                      }`}
                      style={{ textAlign: 'center', verticalAlign: 'middle' }}
                    >
                      {scoreToColumnIndex(score, calculatedRiskGroup) ===
                        cellIndex + 1 && (
                        <span
                          className={classes.dot}
                          style={{
                            backgroundColor: scoreToDotColor(score)
                          }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <p style={{ marginLeft: '1rem' }}>
          Please complete the ATRQ to see your results.
        </p>
      )}
    </BoxWrapper>
  );
};

export default MorningstarClientDifferences;
